import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, Search, Check } from "lucide-react";
import { COUNTRIES, ONRAMP } from "../../data/corridorMap";

const SectionLabel = ({ children }) => (
  <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0a2540]/40">
    {children}
  </h2>
);

// Country code → flag emoji helper
const flagEmoji = (code) =>
  code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");

// Special cases for non-country currency codes
const SPECIAL_FLAGS = {
  EUR: "🇪🇺",
};

// Best-effort: find a representative country name for a currency, purely
// for display (e.g. "United Kingdom · GBP"). Falls back to just the code.
function representativeCountryFor(currency) {
  const entry = Object.entries(COUNTRIES).find(([, c]) => c.currency === currency);
  return entry ? { code: entry[0], name: entry[1].name } : null;
}

// Send-side currencies are whatever Zuba's on-ramp actually supports — NOT
// every currency in the off-ramp COUNTRIES map. A recipient can receive GHS
// or XOF, but a user can't fund a transfer in those currencies today, so
// they must never appear as a "You Send" option.
function getSendCurrencies() {
  return Object.keys(ONRAMP)
    .map((currencyCode) => {
      const rep = representativeCountryFor(currencyCode);
      return {
        code: currencyCode,
        name: rep ? `${rep.name} · ${currencyCode}` : currencyCode,
        flag: SPECIAL_FLAGS[currencyCode] || (rep ? flagEmoji(rep.code) : ""),
      };
    })
    .sort((a, b) => a.code.localeCompare(b.code));
}

export function CurrencyPicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");

  const currencies = useMemo(() => getSendCurrencies(), []);

  const navigationContext = location.state || {
    type: "send",
    currentSend: "GBP",
    currentReceive: "NGN",
  };

  const currentSelected =
    navigationContext.type === "send"
      ? navigationContext.currentSend
      : navigationContext.currentReceive;

  const handleSelection = (chosenCode) => {
    navigate("/transfer", {
      state: {
        selectedSendCurrency:
          navigationContext.type === "send" ? chosenCode : navigationContext.currentSend,
        selectedReceiveCurrency:
          navigationContext.type === "receive" ? chosenCode : navigationContext.currentReceive,
      },
    });
  };

  const filtered = currencies.filter((c) =>
    `${c.code} ${c.name}`.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-24 text-left">

      {/* HEADER — mobile only */}
      <div className="md:hidden bg-[#0A4730] rounded-b-[50px] px-6 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
          >
            <ChevronLeft className="text-white" size={20} />
          </button>
          <div>
            <p className="text-[12px] text-white opacity-80 mb-1">Transfer</p>
            <h1 className="text-2xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              Pick a currency
            </h1>
          </div>
        </div>
      </div>

      {/* DESKTOP HEADER — inline, no green bar */}
      <div className="hidden md:flex items-center gap-3 max-w-xl mx-auto px-5 pt-10 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30"
        >
          <ChevronLeft size={18} />
        </button>
        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
          Pick a currency
        </h1>
      </div>

      {/* CONTENT */}
      <div className="max-w-xl mx-auto px-5 py-8 space-y-8">

        {/* SEARCH */}
        <div className="space-y-2.5">
          <SectionLabel>Search</SectionLabel>
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] px-4 py-3 flex items-center gap-3">
            <Search size={16} className="text-[#0A4730]/30 shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Currency code or country name..."
              className="w-full bg-transparent text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none"
              autoFocus
            />
          </div>
        </div>

        {/* LIST */}
        <div className="space-y-2.5">
          <SectionLabel>
            {navigationContext.type === "send" ? "Send Currency" : "Receive Currency"}
          </SectionLabel>

          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-3 space-y-1">
            {filtered.length === 0 && (
              <p className="text-center text-sm text-[#0A2540]/40 py-10">
                No currencies match your search.
              </p>
            )}

            {filtered.map((currency) => {
              const isSelected = currentSelected === currency.code;
              return (
                <button
                  key={currency.code}
                  onClick={() => handleSelection(currency.code)}
                  className={`w-full flex items-center justify-between gap-4 px-4 py-3.5 rounded-2xl border transition duration-150 text-left ${
                    isSelected
                      ? "bg-[#0A4730]/5 border-[#0A4730]/15"
                      : "bg-transparent border-transparent hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-3.5">
                    <span className="text-xl leading-none select-none">{currency.flag}</span>
                    <div>
                      <p className="text-sm font-bold text-[#0A2540]">{currency.code}</p>
                      <p className="text-xs text-[#0A2540]/40 mt-0.5">{currency.name}</p>
                    </div>
                  </div>

                  {isSelected && (
                    <div className="w-5 h-5 rounded-full bg-[#0A4730] flex items-center justify-center shrink-0">
                      <Check size={11} strokeWidth={3} className="text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}